/**
 * The document that goes inside the message frame, and the URL rules the
 * parent re-applies at click time.
 *
 * ADR 0017 / plan/11 §8.1.4. Kept separate from `MessageFrame.tsx` so the
 * strings that carry the security properties — the CSP, the sandbox token
 * list — can be asserted directly by a test rather than inferred from rendered
 * output.
 *
 * The load-bearing decision is an absence: **the sandbox script-execution
 * token is not granted**. Without it nothing in the frame executes — no `<script>`, no `on*` handler,
 * no `javascript:` URL, no SVG animation — regardless of what survived
 * sanitization. `allow-same-origin` is granted so the parent can reach
 * `contentDocument` to measure height, prepare the links and attach the link
 * handler. Granting the script token alongside it is a full sandbox escape, so
 * the two must never appear together; a tripwire test greps this module for
 * the literal string and fails the build if it is ever added. The same test
 * refuses any top-navigation token: message content never replaces the app.
 */

/**
 * The sandbox token list, as a list, because the runtime assertion compares
 * lists. Exactly these three (ADR 0052, amending ADR 0017), each for one
 * reason:
 *
 *  - `allow-same-origin`: the parent reads and writes the frame's document
 *    from its own realm -- height, fit-to-width, the link preparation below,
 *    the click handler. The frame itself runs nothing with it.
 *  - `allow-popups`: a link the parent has marked `target="_blank"` may open a
 *    new top-level window. WebKit (Safari, iOS, the WebKitGTK desktop app)
 *    runs no event listener at all on a document in a frame without script --
 *    not even one the parent attached -- so the parent's click handler never
 *    fires there, and without this token a link did nothing. Nothing in the
 *    frame can call `window.open`; only a user activating a link can use it.
 *  - `allow-popups-to-escape-sandbox`: without it that window inherits this
 *    sandbox and the destination site loads with no script and an opaque
 *    origin, which in practice is a blank or broken page. It is a normal,
 *    unrelated tab instead: `rel="noopener noreferrer"` leaves it no
 *    `window.opener` and no Referer, so it holds nothing of ours.
 *
 * Not granted, deliberately: script, any top navigation, forms, modals,
 * downloads, pointer lock, presentation, storage access.
 */
export const FRAME_SANDBOX_TOKENS: readonly string[] = [
  'allow-same-origin',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
];

/** The `sandbox` attribute value. */
export const FRAME_SANDBOX = FRAME_SANDBOX_TOKENS.join(' ');

/**
 * The CSP injected as the literal first child of `<head>`, before any sender
 * markup can appear. `<meta>` is stripped from sender content by the server
 * pass, so nothing can precede or override it.
 *
 * `default-src 'none'` is the line that does the work: anything not explicitly
 * re-granted cannot be fetched. `img-src blob:` (ADR 0041, amending ADR 0017)
 * is the one thing that changed when sessions became bound: a subresource the
 * frame loads for itself can carry no `X-Session-Proof`, so the parent fetches
 * every inline part and every sealed remote ref with a proof and hands the
 * frame a `blob:` URL instead (`subresources.ts`). This is *tighter* than the
 * `img-src 'self'` it replaces — `'self'` still let the frame issue a
 * cookie-authenticated GET against our own API, and `blob:` cannot reach the
 * network at all, so the frame is left with no ambient authority whatsoever.
 * A consequence worth stating: the blocked-asset placeholder's
 * `/remote-blocked.png` is stripped by the parent too, because it would now be
 * refused; the box it reserves is drawn from CSS and its width/height
 * attributes, which is all it was ever for.
 *
 * `script-src 'none'`
 * is deliberately redundant with the ungranted sandbox token — two independent
 * mechanisms. `style-src 'unsafe-inline'` is the one relaxation and it is
 * needed for our own reset; it is safe because CSS cannot become script with
 * `script-src 'none'` and no script execution in the frame at all.
 *
 * `frame-ancestors` is omitted: it is header-only and does nothing from a
 * `<meta>`, so listing it would be decoration.
 */
export const FRAME_CSP = [
  "default-src 'none'",
  'img-src blob:',
  "style-src 'unsafe-inline'",
  "script-src 'none'",
  "base-uri 'none'",
  "form-action 'none'",
  "frame-src 'none'",
  "object-src 'none'",
  "child-src 'none'",
  "connect-src 'none'",
  "media-src 'none'",
  "font-src 'none'",
].join('; ');

/**
 * Height clamp (plan/11 §8.1.6). Beyond the maximum the frame keeps its own
 * scrollbar: unbounded growth on a hostile message is a layout DoS.
 *
 * The minimum exists so a message cannot collapse itself to nothing and sit
 * invisibly in the pane; it does not need to be large to do that. It was 200px,
 * which gave every short message -- "Thanks!", "See attached" -- a tall blank
 * block between its one line and its attachments or reply buttons. On a phone,
 * where the reading pane is the whole screen, that read as a rendering fault.
 * 48px still refuses a zero-height frame and fits one line with its padding.
 */
export const MIN_FRAME_HEIGHT = 48;
export const MAX_FRAME_HEIGHT = 20000;
/** Used when measurement throws, with the frame's own scrollbar. */
export const FALLBACK_FRAME_HEIGHT = 600;

/**
 * How long a frame may go without a height before it gets
 * FALLBACK_FRAME_HEIGHT (and the miss is reported). A measurement normally
 * lands within a frame or two of `load`; 3 s is a stalled document, not a
 * slow one, and the reader should not stare at a 48-px sliver for ten
 * (#170: the isolated frame waited 10 s, the srcdoc one forever).
 */
export const SILENT_FRAME_MS = 3_000;

/*
 * The last width a message frame was measured at. The reading pane keeps its
 * width from one message to the next, so it is the best guess for the next
 * frame's width before that frame exists.
 */
let lastFrameWidth = 0;

/** Remembers a frame's width for the next estimate. */
export function noteFrameWidth(px: number): void {
  if (Number.isFinite(px) && px > 0) lastFrameWidth = px;
}

/** For tests: forget the remembered width. */
export function resetFrameWidthForTests(): void {
  lastFrameWidth = 0;
}

/** The frame reset's line box: 14px/1.5, and 4px of body padding top and bottom. */
const LINE_PX = 21;
const BODY_PADDING_PX = 8;
/** An average glyph of the reset's 14-px sans-serif. */
const CHAR_PX = 7;
/** A paragraph's default 1em margin (collapsed between neighbours). */
const PARAGRAPH_GAP_PX = 14;
/**
 * An image, as a message first opens: remote images are blocked until the
 * reader asks for them, and the reset's `height: auto` ignores a declared
 * height, so what shows is a line-high placeholder. A message opened with its
 * images already loaded is a re-render that keeps its measured height.
 */
const IMAGE_PX = LINE_PX;

/**
 * The height a frame starts at, before the document has been measured (#170).
 *
 * It used to start at MIN_FRAME_HEIGHT and jump to the measured height a
 * frame later, pushing everything under the message down: the reading pane's
 * worst layout shift. This estimates the rendered height from the markup —
 * the text in its block boxes, wrapped at the remembered pane width, plus the
 * images by their declared heights — so the jump is a correction, not the
 * whole height. Images count as the placeholder line they are while remote
 * content is blocked. Capped at the viewport: a shift below the fold is not seen,
 * and over-reserving a long message would only move the shift to the shrink.
 * Pure string work, no DOM: it runs during render.
 */
export function estimateFrameHeight(
  html: string,
  width: number = lastFrameWidth || 600,
  viewport: number = typeof window === 'undefined' ? 800 : window.innerHeight,
): number {
  const cap = Math.max(MIN_FRAME_HEIGHT, Math.min(MAX_FRAME_HEIGHT, viewport));
  if (html.trim() === '') return MIN_FRAME_HEIGHT;
  const source = html.length > 200_000 ? html.slice(0, 200_000) : html;
  const images = (source.match(/<img\b/gi)?.length ?? 0) * IMAGE_PX;
  const paragraphs = source.match(/<\/p\s*>/gi)?.length ?? 0;
  const text = source
    .replace(/<(style|script|head|title)\b[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/\s+/g, ' ')
    .replace(/<br\b[^>]*>|<\/(p|div|tr|li|h[1-6]|table|blockquote|pre)\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&[#a-z0-9]+;/gi, 'x');
  const perLine = Math.max(10, Math.floor((width - 4) / CHAR_PX));
  let lines = 0;
  for (const block of text.split('\n')) {
    const length = block.trim().length;
    if (length > 0) lines += Math.ceil(length / perLine);
  }
  const gaps = paragraphs > 0 ? (paragraphs + 1) * PARAGRAPH_GAP_PX : 0;
  const estimate = BODY_PADDING_PX + lines * LINE_PX + gaps + images;
  return Math.min(cap, Math.max(MIN_FRAME_HEIGHT, Math.ceil(estimate)));
}

/**
 * How a message wider than its pane is shown (plan/15 §7.3).
 *
 * `'width'` scales it down to fit, `'actual'` leaves it at its own size and
 * lets it scroll sideways inside the frame. Nothing about the choice reaches
 * the frame as markup: the parent applies it by writing two style properties
 * on the frame's root from this realm, the same way it already reads the
 * height out of it. The CSP and the sandbox above are untouched by it.
 */
export type FrameFit = 'width' | 'actual';

/**
 * The smallest scale fit-to-width will use. Below half size body text is
 * unreadable on a phone, so a message wider than twice the pane stops shrinking
 * here and scrolls the rest horizontally instead.
 */
export const MIN_FIT_ZOOM = 0.5;

/** What the parent applies to the frame document for one measurement. */
export interface FrameFitResult {
  /** CSS `zoom` for the frame's root; 1 means none. */
  zoom: number;
  /** Whether what is left over scrolls sideways inside the frame. */
  scrollX: boolean;
}

/**
 * Decides the scale for a body that lays out `contentWidth` px wide in a frame
 * `viewportWidth` px wide.
 *
 * A pure function of two numbers read out of the frame, so it is the part a
 * test can pin down; jsdom lays nothing out and reports every width as 0,
 * which lands in the "nothing to fit" branch by design.
 */
export function fitFrame(contentWidth: number, viewportWidth: number, fit: FrameFit): FrameFitResult {
  const measurable =
    Number.isFinite(contentWidth) &&
    Number.isFinite(viewportWidth) &&
    contentWidth > 0 &&
    viewportWidth > 0;
  // One pixel of slack: sub-pixel rounding must not shrink a message that fits.
  if (!measurable || contentWidth <= viewportWidth + 1) return { zoom: 1, scrollX: false };
  if (fit === 'actual') return { zoom: 1, scrollX: true };
  const zoom = viewportWidth / contentWidth;
  if (zoom < MIN_FIT_ZOOM) return { zoom: MIN_FIT_ZOOM, scrollX: true };
  return { zoom, scrollX: false };
}

/**
 * Our reset. A message renders on a white canvas in a light app; in a dark app
 * it renders dark (ADR 0081, amending plan/11 §8.1.7's "always a white card"):
 * the dark rules below, the sender's own dark rules, or the adaptation in
 * `darkAdapt.ts` -- all screen-only, so paper stays light.
 *
 * The frame inherits the app shell's CSP as well as its own, and there this
 * `<style>` is allowed by its SHA-256 (`style-src-elem`,
 * frontend/securityHeaders.ts, ADR 0049). Change a byte here and update that
 * hash; src/securityHeaders.test.ts fails and names the new one.
 */
export const FRAME_RESET = `
:root { color-scheme: light; }
html, body {
  margin: 0;
  padding: 0;
  background: #ffffff;
  color: #1a1a1a;
  overflow-x: hidden;
}
body {
  padding: 4px 2px;
  font: 14px/1.5 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  overflow-wrap: anywhere;
  word-break: break-word;
}
img { max-width: 100%; height: auto; border: 0; }
/*
 * A blocked remote asset (ADR 0018 §1.4). The server emits
 * <img class="ols-remote-placeholder" src="/remote-blocked.png" width height alt>
 * with the sender's dimensions clamped to [16,2000], so the box reserves the
 * space the real image would take and the layout does not jump when the user
 * loads it. The height:auto rule above would collapse a 1x1 transparent PNG
 * to nothing, which is exactly the jump this rule exists to prevent, so the
 * placeholder keeps the height it was given.
 *
 * Under img-src blob: the parent strips that src before injection: the
 * placeholder is decoration for a request that was deliberately not made, so
 * the dashed box below — sized by the width/height attributes the server kept
 * — is the whole of it. The alt text shows inside the box, which is more
 * honest than a picture of nothing.
 *
 * A scored tracker gets NO placeholder: it is removed outright, counted, and
 * never fetched under any rule. A pixel has no visual job, and a box per pixel
 * would litter the message.
 */
img.ols-remote-placeholder {
  height: revert;
  background: #f3f3f3;
  outline: 1px dashed #c9c9c9;
  outline-offset: -1px;
}
/*
 * A subresource the parent tried to fetch and could not (ADR 0041). Visually
 * distinct from the placeholder above on purpose: a dashed grey box means "we
 * chose not to load this", a solid warm box means "we tried and failed". They
 * are different facts and the reading pane says so in words as well.
 */
img.ols-subresource-failed {
  height: revert;
  background: #fdf2f2;
  outline: 1px solid #dcaeae;
  outline-offset: -1px;
}
table { max-width: 100%; }
a { color: #1a4fd6; }
blockquote { margin: 0 0 0 12px; padding-left: 12px; border-left: 2px solid #d5d5d5; }
/*
 * The message root (ADR 0081). Both renderers wrap the body in it, and every
 * rule of the sender's stylesheet was rewritten to start here, so a sender
 * selector can only ever match the sender's own markup.
 */
.ols-msg { display: block; }
/*
 * Dark mode inside the message (ADR 0081). The root carries ols-dark when
 * the reader's app is dark and they have not asked for this message's
 * original colours; the sender's own dark rules hang off the same class, and
 * ols-dark-auto additionally has the colours adapted by script. All of it
 * is screen-only: printed, a message is always its light original.
 */
@media screen {
  :root.ols-dark { color-scheme: dark; }
  :root.ols-dark, :root.ols-dark body { background: #171c23; color: #e3e7ee; }
  :root.ols-dark a { color: #8fb4ff; }
  :root.ols-dark blockquote { border-left-color: #3d4652; }
  :root.ols-dark img.ols-remote-placeholder { background: #272e38; outline-color: #4b5563; }
  :root.ols-dark img.ols-subresource-failed { background: #3b2426; outline-color: #7a4b4f; }
}
/*
 * What sender CSS may not undo: the boxes that say an image was blocked or
 * failed. A cascade layer, because a layered !important beats the sender's
 * unlayered one, and the CSS pass refuses sender @layer rules.
 */
@layer ols-app {
  img.ols-remote-placeholder, img.ols-subresource-failed {
    visibility: visible !important;
    opacity: 1 !important;
    outline-style: dashed !important;
    outline-width: 1px !important;
  }
  img.ols-subresource-failed { outline-style: solid !important; }
}
`.trim();

/**
 * Escapes text destined for an HTML text position. Only used for the `<title>`
 * below; the body HTML is inserted as markup because it has already been
 * through the server sanitizer, which is the only thing allowed to produce it.
 */
function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * How the frame document is coloured (ADR 0081).
 *
 *  - `light`: the sender's own colours on a white canvas. A light app, or a
 *    dark one where the reader chose "Show original colors".
 *  - `dark`: the app is dark and the sender styled the message for dark: the
 *    root carries `ols-dark`, which the reset's dark canvas and the sender's
 *    own (rewritten) `prefers-color-scheme: dark` rules hang off.
 *  - `dark-auto`: the app is dark and the sender did not: `ols-dark` plus
 *    `ols-dark-auto`, and the page adapts the colours (`darkAdapt.ts`).
 */
export type FrameAppearance = 'light' | 'dark' | 'dark-auto';

/** The root's class attribute for an appearance; a fixed string, never sender input. */
function rootOpenTag(appearance: FrameAppearance): string {
  switch (appearance) {
    case 'dark':
      return '<html class="ols-dark">';
    case 'dark-auto':
      return '<html class="ols-dark ols-dark-auto">';
    default:
      return '<html>';
  }
}

/** The class of the element the body is wrapped in; the sender's stylesheet is scoped to it. */
export const MESSAGE_ROOT_CLASS = 'ols-msg';

/**
 * The frame's own background for an appearance, painted on the iframe element
 * too so nothing flashes the wrong colour before the document loads. The
 * dark one is the reset's dark canvas.
 */
export function frameCanvas(appearance: FrameAppearance): { background: string; colorScheme: 'light' | 'dark' } {
  return appearance === 'light'
    ? { background: '#ffffff', colorScheme: 'light' }
    : { background: '#171c23', colorScheme: 'dark' };
}

/**
 * Builds the `srcdoc` document. `html` must be `MessageDetail.bodyHtml` — the
 * output of the server sanitizer — and nothing else.
 *
 * The sender's stylesheet (`MessageDetail.bodyCss`) is NOT written here: this
 * document inherits the app shell's CSP, whose `style-src-elem` admits only
 * the reset above by its hash, so an inline `<style>` of the sender's would be
 * refused. The page adopts it as a constructed stylesheet on load instead
 * (`adoptStylesheet`), which CSP does not govern.
 */
export function buildFrameDocument(html: string, appearance: FrameAppearance = 'light'): string {
  return [
    '<!doctype html>',
    rootOpenTag(appearance),
    '<head>',
    `<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">`,
    '<meta name="referrer" content="no-referrer">',
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${escapeText('Message body')}</title>`,
    `<style>${FRAME_RESET}</style>`,
    '</head>',
    '<body>',
    `<div class="${MESSAGE_ROOT_CLASS}">`,
    html,
    '</div>',
    '</body>',
    '</html>',
  ].join('');
}

/**
 * The URL schemes a link may use, re-checked in the parent realm at click
 * time. This is the third check on the same value (server pre-pass, server
 * allowlist, here) and it is cheap.
 */
const OPENABLE_SCHEMES = new Set(['http:', 'https:']);

/**
 * Normalizes a URL the way the server pass does (plan/02 §2.4): strip NUL and
 * every C0/C1 control character including tab, CR and LF, then trim leading
 * and trailing whitespace including Unicode spaces. A tab or a NUL smuggled
 * into the scheme collapses away here rather than needing a denylist entry of
 * its own.
 */
export function normalizeHref(raw: string): string {
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\u0000-\u001f\u007f-\u009f]/g, '').trim();
}

/**
 * Decides what a click on `href` may do.
 *
 * Returns a `URL` only for `http:`/`https:`, and only after parsing succeeds —
 * the parent opens that URL itself, from its own realm, after a confirm, or
 * (where the parent's handler cannot run) marks the link so the engine opens
 * exactly that URL in a new window (`prepareFrameLinks`). Everything else is
 * not opened: `mailto:` goes to the composer and `#ols-msg-…` scrolls the
 * pane (`fragmentTargetID`), each by its own path, and anything left over is
 * inert.
 */
export function openableURL(href: string | null | undefined): URL | null {
  if (!href) return null;
  const normalized = normalizeHref(href);
  if (!normalized) return null;
  let url: URL;
  try {
    url = new URL(normalized);
  } catch {
    return null; // relative, or not a URL at all
  }
  if (!OPENABLE_SCHEMES.has(url.protocol)) return null;
  return url;
}

/**
 * The one same-document link a message may carry (ADR 0101, plan/02 §2.7):
 * `#ols-msg-<id>`, into the namespace the server sanitizer moved every sender
 * id to (`mail.FragmentPrefix`). Returns the target element's id, or null for
 * anything else -- a bare `#`, a fragment the server would not have written --
 * which stays as inert as every non-http(s) link.
 */
export function fragmentTargetID(href: string | null | undefined): string | null {
  const m = /^\s*#(ols-msg-[A-Za-z0-9_.:-]{1,64})\s*$/.exec(href ?? '');
  return m?.[1] ?? null;
}

/**
 * Where `target` sits in its document, as a fraction of the body's height
 * (0 at the top, 1 at the bottom). A fraction rather than pixels so the
 * frame's fit-to-width zoom, which scales the document but not the numbers
 * the two sides measure in, cannot put the scroll in the wrong place.
 */
export function fragmentPosition(doc: Document, target: Element): number {
  const body = doc.body?.getBoundingClientRect();
  if (!body || !(body.height > 0)) return 0;
  const at = (target.getBoundingClientRect().top - body.top) / body.height;
  return Number.isFinite(at) ? Math.min(1, Math.max(0, at)) : 0;
}

/**
 * Scrolls whatever scrolls the reading pane so the point `at` (a fraction of
 * the frame's height) is at the top of the view. The frame is as tall as the
 * message and never scrolls itself, so a fragment link moves the pane, not the
 * frame: the nearest ancestor that scrolls (the pane on desktop, the
 * conversation list), or the document on a phone, where the page scrolls.
 */
export function scrollFrameTo(frame: HTMLElement, at: number): void {
  const fraction = Number.isFinite(at) ? Math.min(1, Math.max(0, at)) : 0;
  const frameBox = frame.getBoundingClientRect();
  const offset = frameBox.top + fraction * frameBox.height;
  const reduce =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior: ScrollBehavior = reduce ? 'auto' : 'smooth';
  for (let el = frame.parentElement; el; el = el.parentElement) {
    const overflowY = window.getComputedStyle(el).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
      const viewTop = el.getBoundingClientRect().top;
      el.scrollTo({ top: el.scrollTop + offset - viewTop - coveredBelow(frame, viewTop), behavior });
      return;
    }
  }
  window.scrollTo({ top: window.scrollY + offset - coveredBelow(frame, 0), behavior });
}

/**
 * How much of the view below `viewTop` a sticky or fixed bar covers -- the
 * phone's reading app bar, which is sticky over the scrolling document -- so
 * the target lands under it rather than behind it. Measured, not a constant:
 * whatever is pinned over the top edge where the frame is.
 */
function coveredBelow(frame: HTMLElement, viewTop: number): number {
  if (typeof document.elementsFromPoint !== 'function') return 0;
  const x = frame.getBoundingClientRect().left + 1;
  let bottom = viewTop;
  for (const hit of document.elementsFromPoint(x, viewTop + 1)) {
    for (let el: Element | null = hit; el && el !== document.body; el = el.parentElement) {
      if (el === frame) break;
      const position = window.getComputedStyle(el).position;
      if (position === 'sticky' || position === 'fixed') {
        bottom = Math.max(bottom, el.getBoundingClientRect().bottom);
        break;
      }
    }
  }
  return bottom - viewTop;
}

/** Where a prepared link opens: a new top-level window, never this frame or the app. */
export const FRAME_LINK_TARGET = '_blank';

/**
 * What a prepared link opens it with. `noopener` leaves the new page no
 * `window.opener` (a `_blank` target implies it in current engines; a named
 * one does not, so it is stated), and `noreferrer` sends no Referer, which
 * would otherwise carry the account and message ids in our path.
 */
export const FRAME_LINK_REL = 'noopener noreferrer';

/**
 * Prepares every link in the frame document so the engine can open it by
 * itself, safely, where the parent's click handler never runs (ADR 0052).
 *
 * Runs in the parent realm on the frame's `load`, against a document the frame
 * cannot script. For each `<a>`:
 *
 *  - an `href` that `openableURL` accepts is rewritten to the URL *it* parsed
 *    -- so the engine navigates to exactly what this realm validated, not to
 *    its own parse of the raw attribute -- and gets `target="_blank"`,
 *    `rel="noopener noreferrer"` and a `title` of the real destination. The
 *    title is what is left of the confirm on an engine without one: hovering a
 *    link shows where it goes, whatever its text claims;
 *  - anything else loses any `target` and `rel`. The sanitizer never emits
 *    them, but with popups allowed a target on a `mailto:` or a `javascript:`
 *    link is exactly what must not open, so this does not rely on it. Such a
 *    link stays as inert as it was.
 *
 * Where the click handler does run (Chromium), it cancels the click first,
 * so the target is never used and the confirm path is unchanged.
 */
export function prepareFrameLinks(doc: Document): void {
  for (const anchor of Array.from(doc.querySelectorAll('a'))) {
    const url = openableURL(anchor.getAttribute('href'));
    if (!url) {
      anchor.removeAttribute('target');
      anchor.removeAttribute('rel');
      continue;
    }
    anchor.setAttribute('href', url.href);
    anchor.setAttribute('target', FRAME_LINK_TARGET);
    anchor.setAttribute('rel', FRAME_LINK_REL);
    anchor.setAttribute('title', url.href);
  }
}

/**
 * The URL a link in the frame would open, re-validated in this realm, for the
 * URL preview (ADR 0053). Null for anything `openableURL` refuses, so a
 * `mailto:`, `javascript:` or relative link shows no preview at all.
 *
 * Duck-typed rather than `instanceof Element` for the same reason as the click
 * handler: the frame is another realm. It reads the `href` attribute, which
 * `prepareFrameLinks` has already rewritten to the URL this realm parsed, and
 * never the link's text, which is the part a sender controls.
 */
export function frameLinkURL(node: unknown): URL | null {
  const el = node as { closest?: (sel: string) => Element | null } | null;
  if (!el || typeof el.closest !== 'function') return null;
  const anchor = el.closest('a');
  return anchor ? openableURL(anchor.getAttribute('href')) : null;
}

/**
 * Every distinct openable URL among the links a non-collapsed selection in
 * `doc` touches, in document order. A selection wholly inside a link's text
 * counts as that link.
 */
export function selectedLinkURLs(doc: Document): URL[] {
  const selection = doc.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return [];
  const ranges: Range[] = [];
  for (let i = 0; i < selection.rangeCount; i++) ranges.push(selection.getRangeAt(i));
  const seen = new Set<string>();
  const urls: URL[] = [];
  for (const anchor of Array.from(doc.querySelectorAll('a[href]'))) {
    if (!ranges.some((range) => range.intersectsNode(anchor))) continue;
    const url = openableURL(anchor.getAttribute('href'));
    if (!url || seen.has(url.href)) continue;
    seen.add(url.href);
    urls.push(url);
  }
  return urls;
}

/** A URL cut where the preview emphasises it. The four parts concatenate to `href`. */
export interface LinkURLParts {
  /** `https://` */
  scheme: string;
  /** `user:pass@`, or empty. Shown, because it is the classic way to fake a host. */
  userinfo: string;
  /** The host and any non-default port: the answer to "where does this go". */
  host: string;
  /** Path, query and fragment. */
  rest: string;
}

/**
 * Splits a validated `http(s)` URL for the preview. `URL` has already put the
 * host in its ASCII (punycode) form and percent-encoded every non-ASCII
 * character elsewhere, so what is shown is ASCII: a lookalike host reads as
 * `xn--…` and no bidi control can reorder it.
 */
export function linkURLParts(url: URL): LinkURLParts {
  const scheme = `${url.protocol}//`;
  const userinfo =
    url.username || url.password ? `${url.username}${url.password ? `:${url.password}` : ''}@` : '';
  const host = url.host;
  const rest = url.href.slice(scheme.length + userinfo.length + host.length);
  return { scheme, userinfo, host, rest };
}
